import { queryBuilder } from '@inpart-io/devtools-iap';
import {
    CASCADE_DELETE_DATA_OBJECT,
    CREATE_DATA_OBJECT,
    RETRIEVE_BY_ID_LIST,
    RETRIEVE_BY_QUERY,
    UPDATE_DATA_OBJECT,
    useMessageDisplayer
} from '@inpart-io/devtools-vue';
import { type IPrivateContact } from '@inpart-io/padme-iap';
import { internalContactProperties, privateFieldCustomListValueProperties } from '@inpart-io/padme-iap';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import { INTERNAL_CONTACT_MODULE } from '@/store/internal-contact-module';
import { PRIVATE_FIELD_CUSTOM_VALUE_MODULE } from '@/store/private-field-custom-value-module';

type IPayload = Omit<IPrivateContact, 'description'> & { businessUnit?: null | number };

interface IUseInternalContacts {
    createInternalContact: (payload: IPayload) => Promise<number[]>;
    removeInternalContact: (id: number) => Promise<void>;
    updateInternalContact: (payload: IPayload) => Promise<void>;
}

export function useInternalContacts(): IUseInternalContacts {
    const store = useStore();
    const { t } = useI18n();
    const messageDisplayer = useMessageDisplayer();

    const createInternalContact = async (payload: IPayload): Promise<number[]> => {
        try {
            const ids = await store.dispatch(`${INTERNAL_CONTACT_MODULE}/${CREATE_DATA_OBJECT}`, {
                dataObjectList: [payload],
                properties: internalContactProperties.DEFAULT
            });

            await store.dispatch(`${INTERNAL_CONTACT_MODULE}/${RETRIEVE_BY_ID_LIST}`, {
                idList: ids,
                properties: internalContactProperties.DEFAULT
            });

            messageDisplayer.success(t('NOTIFS.CREATE_SUCCESS', { object: t('INTERNAL_CONTACT.CAP.SING') }));

            return ids;
        } catch (e) {
            return e;
        }
    };

    const removeInternalContact = async (id: number): Promise<void> => {
        await store.dispatch(`${INTERNAL_CONTACT_MODULE}/${CASCADE_DELETE_DATA_OBJECT}`, {
            dobjId: id,
            properties: internalContactProperties.DEFAULT
        });

        messageDisplayer.success(t('REMOVE_SUCCESS'));
    };

    const updateInternalContact = async (payload: IPayload): Promise<void> => {
        await store.dispatch(`${INTERNAL_CONTACT_MODULE}/${UPDATE_DATA_OBJECT}`, {
            dataObjectList: [payload],
            properties: internalContactProperties.DETAILS
        });

        if (payload.businessUnit) {
            await store.dispatch(`${PRIVATE_FIELD_CUSTOM_VALUE_MODULE}/${RETRIEVE_BY_QUERY}`, {
                query: queryBuilder.build(queryBuilder.op.or(queryBuilder.cmp.isIn('dobjId', [payload.businessUnit]))),
                properties: privateFieldCustomListValueProperties.DEFAULT
            });
        }

        messageDisplayer.success(t('NOTIFS.UPDATE_SUCCESS', { object: t('INTERNAL_CONTACT.CAP.SING') }));
    };

    return { createInternalContact, removeInternalContact, updateInternalContact };
}
